"use client"

import { useCareers } from "@/hook/useCareers"
import { UseServiceSidebarOptions, UseServiceSidebarReturn } from "@/types/types"
import { useEffect, useMemo, useRef, useState } from "react"

export const useServiceSidebar = (options: UseServiceSidebarOptions = {}): UseServiceSidebarReturn => {
  const { activeContentType, locale = "vi" } = options
  const { careers, loading, error, fetchCareers, setLocale } = useCareers()
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({})

  // Track previous values to avoid unnecessary fetches
  const prevContentTypeRef = useRef<string | undefined>(undefined)
  const prevLocaleRef = useRef<string>(locale)

  // Memoize active category contentType to avoid unnecessary re-renders
  const memoizedContentType = useMemo(() => activeContentType, [activeContentType])

  // Fetch careers when active category is career type
  useEffect(() => {
    const contentTypeChanged = prevContentTypeRef.current !== memoizedContentType
    const localeChanged = prevLocaleRef.current !== locale
    const isCareerContent = memoizedContentType === "career"

    if (isCareerContent && (contentTypeChanged || localeChanged)) {
      setLocale(locale)
      fetchCareers(locale)
      prevContentTypeRef.current = memoizedContentType
      prevLocaleRef.current = locale
    } else if (!isCareerContent) {
      // Reset ref when not career content
      prevContentTypeRef.current = memoizedContentType
    }
  }, [memoizedContentType, locale, setLocale, fetchCareers])

  return {
    careers: careers || [],
    loading,
    error,
    expandedMap,
    setExpandedMap,
    fetchCareers,
    setLocale,
  }
}
